import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import FloatingCart from './components/cart/FloatingCart'
import { Suspense, lazy } from 'react'

// Layout components
import { PublicLayout } from './components/layouts/PublicLayout'
import { AdminLayout } from './components/layouts/AdminLayout'
import { ClientLayout } from './components/layouts/ClientLayout'
import { LoadingSpinner } from './components/ui/LoadingSpinner'

// Lazy load pages for better performance
const HomePageCerebras = lazy(() => import('./pages/public/HomePageCerebras'))
const HomePageClean = lazy(() => import('./pages/public/HomePageClean'))
const HomePageWebstore3 = lazy(() => import('./pages/public/HomePageWebstore3'))
const HomePageWebstore2 = lazy(() => import('./pages/public/HomePageWebstore2'))
const HomePageModern = lazy(() => import('./pages/public/HomePageModern'))
const HomePageWebstore = lazy(() => import('./pages/public/HomePageWebstore'))
const HomePageNew = lazy(() => import('./pages/public/HomePageNew'))
const HomePage = lazy(() => import('./pages/public/HomePage'))
const CartPage = lazy(() => import('./pages/public/CartPage'))

// Sitio público estilo Metalza (chefmax-front-metalza-20261004)
const SiteLayout = lazy(() => import('./components/site/SiteLayout'))
const SiteHomePage = lazy(() => import('./pages/site/SiteHomePage'))
const SiteTiendaPage = lazy(() => import('./pages/site/SiteTiendaPage'))
const SiteCategoriaPage = lazy(() => import('./pages/site/SiteCategoriaPage'))
const SiteNosotrosPage = lazy(() => import('./pages/site/SiteNosotrosPage'))
const SiteContactoPage = lazy(() => import('./pages/site/SiteContactoPage'))
const SitePromocionesPage = lazy(() => import('./pages/site/SitePromocionesPage'))
const SiteCotizarPage = lazy(() => import('./pages/site/SiteCotizarPage'))
const SiteCatalogoPage = lazy(() => import('./pages/site/SiteCatalogoPage'))
const SiteProyectoPage = lazy(() => import('./pages/site/SiteProyectoPage'))
const SiteBrochurePage = lazy(() => import('./pages/site/SiteBrochurePage'))
const SiteFranquiciasPage = lazy(() => import('./pages/site/SiteFranquiciasPage'))
const SiteFranchiseBrochurePage = lazy(() => import('./pages/site/SiteFranchiseBrochurePage'))
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'))

// Admin pages
const CatalogPage = lazy(() => import('./pages/admin/CatalogPage'))
const CortexCatalogProcessorPage = lazy(() => import('./pages/admin/CortexCatalogProcessorPage'))
const InventoryPage = lazy(() => import('./pages/admin/InventoryPage'))
const SuppliersPage = lazy(() => import('./pages/admin/SuppliersPage'))
const ProductsPage = lazy(() => import('./pages/admin/ProductsPage'))
const QuotationsPage = lazy(() => import('./pages/admin/QuotationsPage'))
const SalesPage = lazy(() => import('./pages/admin/SalesPage'))
const ProspectsPage = lazy(() => import('./pages/admin/ProspectsPage'))
const CotizaIaPage = lazy(() => import('./pages/admin/CotizaIaPage'))
const UsersPage = lazy(() => import('./pages/admin/UsersPage'))
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'))

// Client pages
const ClientDashboard = lazy(() => import('./pages/client/ClientDashboard'))
const MyOrdersPage = lazy(() => import('./pages/client/MyOrdersPage'))
const OrderDetailsPage = lazy(() => import('./pages/client/OrderDetailsPage'))

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Admin route component
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (user.role !== 'admin' && user.role !== 'superadmin') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// Client route component
const ClientRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (user.role !== 'client') {
    // Redirect admins to admin panel, others to home
    if (user.role === 'admin' || user.role === 'superadmin') {
      return <Navigate to="/admin" replace />
    }
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <div className="App">
      <Suspense 
        fallback={
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        }
      >
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<PublicLayout />}>
            {/* Sitio público estilo Metalza */}
            <Route element={<SiteLayout />}>
              <Route index element={<SiteHomePage />} />
              <Route path="tienda" element={<SiteTiendaPage />} />
              <Route path="categoria/:slug" element={<SiteCategoriaPage />} />
              <Route path="nosotros" element={<SiteNosotrosPage />} />
              <Route path="contacto" element={<SiteContactoPage />} />
              <Route path="promociones" element={<SitePromocionesPage />} />
              <Route path="cotizar" element={<SiteCotizarPage />} />
              <Route path="catalogo" element={<SiteCatalogoPage />} />
              <Route path="proyecto" element={<SiteProyectoPage />} />
              <Route path="brochure/:token" element={<SiteBrochurePage />} />
              <Route path="franquicias" element={<SiteFranquiciasPage />} />
              <Route path="franquicia/:token" element={<SiteFranchiseBrochurePage />} />
            </Route>
            {/* Landing anterior (legacy) */}
            <Route path="cerebras" element={<HomePageCerebras />} />
            <Route path="webstore3" element={<HomePageWebstore3 />} />
            <Route path="clean" element={<HomePageClean />} />
            <Route path="webstore2" element={<HomePageWebstore2 />} />
            <Route path="modern" element={<HomePageModern />} />
            <Route path="webstore" element={<HomePageWebstore />} />
            <Route path="home-new" element={<HomePageNew />} />
            <Route path="cart" element={<CartPage />} />
            <Route path="home-old" element={<HomePage />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
          </Route>

          {/* Admin routes */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<Navigate to="catalog" replace />} />
            <Route path="catalog" element={<CatalogPage />} />
            {/* Dashboard duplicado del catálogo — redirect, no 404 (links viejos) */}
            <Route path="dashboard" element={<Navigate to="/admin/catalog" replace />} />
            <Route path="erp" element={<Navigate to="/admin/catalog" replace />} />
            <Route path="digestor" element={<CortexCatalogProcessorPage />} />
            <Route path="cortex-ai" element={<CortexCatalogProcessorPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="suppliers" element={<SuppliersPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="quotations" element={<QuotationsPage />} />
            <Route path="sales" element={<SalesPage />} />
            <Route path="prospects" element={<ProspectsPage />} />
            <Route path="cotizaia" element={<CotizaIaPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Client routes */}
          <Route path="/client" element={<ClientRoute><ClientLayout /></ClientRoute>}>
            <Route index element={<ClientDashboard />} />
            <Route path="orders" element={<MyOrdersPage />} />
            {/* <Route path="orders/:id" element={<OrderDetailsPage />} /> */}
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        {/* Floating Cart - Disabled in favor of CartModal */}
        {/* <FloatingCart /> */}
      </Suspense>
    </div>
  )
}

export default App
